import { map, compact, get, keys } from 'lodash';

import BooleanExperimentCampaign from 'models/BooleanExperimentCampaign';
import CartLineItem from 'models/CartLineItem';
import * as actions from 'state/actions';
import ACTIONS from 'state/constants';
import CartSelector from 'state/selectors/cart';
import Catalog from 'state/selectors/catalog';
import { getCustomCollectionProductsData } from 'state/selectors/collection';
import CollectionPage from 'state/selectors/collection-page';
import { getCurrentProduct, getCurrentVariant } from 'state/selectors/pdp';
import PersonalizationsSelector from 'state/selectors/personalizations';
import AnalyticsMediatorSingleton from 'utils/AnalyticsMediatorSingleton';
import { CURRENCIES } from 'utils/Money';
import Rudderstack from 'utils/Rudderstack';
import GENERIC_CTA_CLICKED_EVENT from 'utils/Rudderstack/constants';
import Settings from 'utils/Settings';

const getItem = (state, id) => get(state, ['ajaxCart', 'data', 'entities', 'item', id]);

const productListFiltered = (store, next, action) => {
  next(action);
  const state = store.getState();

  const collectionHandle = CollectionPage.getCollectionHandle(state);
  const parentCollection = Catalog.getParentCollection(state, collectionHandle);
  const activeFilters = CollectionPage.getAppliedFilterOptions(state);
  const collectionItems = CollectionPage.getCollectionItems(state, parentCollection.masterProducts);
  const collectionInfo = CollectionPage.getCollectionMetadata(
    state,
    collectionHandle,
    parentCollection
  );

  Rudderstack.productListFiltered({
    collection: collectionInfo,
    activeFilters,
    collectionItems,
  });
};

const productListViewed = (store, next, action) => {
  next(action);
  const state = store.getState();

  const collectionHandle = CollectionPage.getCollectionHandle(state);
  const parentCollection = Catalog.getParentCollection(state, collectionHandle);
  const collectionItems = CollectionPage.getCollectionItems(state, parentCollection.masterProducts);
  const collectionInfo = CollectionPage.getCollectionMetadata(
    state,
    collectionHandle,
    parentCollection
  );

  Rudderstack.productListViewed({
    collection: collectionInfo,
    collectionItems,
  });
};

const customCollectionProductListViewed = (store, next, action) => {
  next(action);
  const state = store.getState();

  const customCollectionProductsData = compact(getCustomCollectionProductsData(state));

  Rudderstack.productListViewed({
    collection: CollectionPage.getCustomCollectionMetadata(
      state,
      customCollectionProductsData.length
    ),
    collectionItems: customCollectionProductsData,
  });
};

const viewProductDetails = (store, next, action) => {
  next(action);
  const state = store.getState();

  const product = getCurrentProduct(state);
  const variant = getCurrentVariant(state);

  // check if current product is set yet
  if (!product.masterId) {
    return;
  }

  /**
   *
   * Warning! - Mediator was disabled and replaced with Rudderstack V3
   * TODO: All code under Settings.isNextGenAnalyticsEnabled shuld be removed
   *
   */
  if (Settings.isNextGenAnalyticsEnabled) {
    const analytics = AnalyticsMediatorSingleton.get();
    const formattedProduct = variant
      ? Rudderstack._formatProductWithVariant(product, variant)
      : Rudderstack._formatMasterProduct(product);

    const currencyCode = Settings.currency;
    const meta = {
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
    };

    analytics.productViewed({ product: formattedProduct, meta });
  } else {
    Rudderstack.viewProductDetails({
      product,
      variant,
    });
  }
};

const addToCart = (store, next, action) => {
  next(action);

  const state = store.getState();
  const { addedItems, origination } = action.payload;

  const cartLineItems = CartSelector.getCartLineItems(state);

  const itemIds = keys(addedItems);

  return map(itemIds, itemId => {
    const addedCartLineItem = new CartLineItem(getItem(state, itemId));
    const addedCartLineItemQuantity = addedItems[itemId];

    /**
     *
     * Warning! - Mediator was disabled and replaced with Rudderstack V3
     * TODO: All code under Settings.isNextGenAnalyticsEnabled shuld be removed
     *
     */
    if (Settings.isNextGenAnalyticsEnabled) {
      const analytics = AnalyticsMediatorSingleton.get();
      const cartProducts = map(cartLineItems, (lineItem, index) =>
        Rudderstack._formatCartLineItem(lineItem, { position: index })
      );

      const currencyCode = Settings.currency;
      const meta = {
        currency: currencyCode,
        currency_symbol: CURRENCIES[currencyCode].units.symbol,
        cart_added_trigger_source: origination,
      };

      const addedProduct = Rudderstack._formatCartLineItem(addedCartLineItem, {
        quantity: addedCartLineItemQuantity,
      });

      analytics.productAdded({ cartProducts: cartProducts, addedProduct, meta });
    } else {
      Rudderstack.productAdded({
        cartLineItems,
        addedCartLineItem,
        addedCartLineItemQuantity,
        origination,
      });
    }
  });
};

const removeFromCart = (store, next, action) => {
  const state = store.getState();
  const itemId = get(action, ['payload', 'removedItem']);
  const removedCartLineItem = new CartLineItem(getItem(state, itemId));
  const removedCartLineItemQuantity = removedCartLineItem.quantity;

  next(action);

  const updatedState = store.getState();

  const cartLineItems = CartSelector.getCartLineItems(updatedState);

  Rudderstack.productRemoved({
    cartLineItems,
    removedCartLineItem,
    removedCartLineItemQuantity,
  });
};

const updateLineItem = (store, next, action) => {
  const state = store.getState();
  const itemId = get(action, ['payload', 'updatedItem']);
  const removedCartLineItem = new CartLineItem(getItem(state, itemId));
  const removedCartLineItemQuantity = 1; // this only ever removes 1 item

  next(action);

  const updatedState = store.getState();

  const cartLineItems = CartSelector.getCartLineItems(updatedState);

  /**
   *
   * Warning! - Mediator was disabled and replaced with Rudderstack V3
   * TODO: All code under Settings.isNextGenAnalyticsEnabled shuld be removed
   *
   */
  if (Settings.isNextGenAnalyticsEnabled) {
    const analytics = AnalyticsMediatorSingleton.get();
    const cartProducts = map(cartLineItems, (lineItem, index) =>
      Rudderstack._formatCartLineItem(lineItem, { position: index })
    );
    const removedProduct = Rudderstack._formatCartLineItem(removedCartLineItem, {
      quantity: removedCartLineItemQuantity,
    });

    const currencyCode = Settings.currency;
    const meta = {
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
    };

    analytics.productRemoved({ cartProducts, removedProduct, meta });
  } else {
    Rudderstack.productRemoved({
      cartLineItems,
      removedCartLineItem,
      removedCartLineItemQuantity,
    });
  }
};

const openCart = (store, next, action) => {
  next(action);

  if (get(action, 'payload')) {
    const state = store.getState();

    const cartLineItems = CartSelector.getCartLineItems(state);

    Rudderstack.cartViewed({
      cartLineItems,
    });
  }
};

const experimentsViewed = (store, next, action) => {
  next(action);
  const state = store.getState();

  const activeCampaigns = PersonalizationsSelector.getAllCampaigns(state);

  Object.entries(activeCampaigns).forEach(([campaignName, campaign]) => {
    const experiment = new BooleanExperimentCampaign({
      campaign,
      experimentIdentifier: campaignName,
    });

    const analyticsData = experiment.variation?.analyticsMetadata;

    // check for any variation to be returned
    if (analyticsData) {
      Rudderstack.experimentViewed({
        analyticsData,
        experimentType: 'API',
      });

      store.dispatch(actions.trackDYAPICampaignAssignment(analyticsData));
    }
  });
};

const globalBannerClicked = (store, next, action) => {
  const payload = get(action, ['payload']);
  const { message } = payload;

  if (message) {
    Rudderstack.globalBannerClicked({
      message,
    });
  }
};

const trackCollectionPillToggleClick = (store, next, action) => {
  next(action);
  const state = store.getState();

  const collectionHandle = CollectionPage.getCollectionHandle(state);
  const parentCollection = Catalog.getParentCollection(state, collectionHandle);
  const collectionInfo = CollectionPage.getCollectionMetadata(
    state,
    collectionHandle,
    parentCollection
  );
  const { tab } = action.payload;

  Rudderstack.productListFeatureClicked({
    featureCTA: tab?.text,
    featureLinkDestination: tab?.href,
    featureType: GENERIC_CTA_CLICKED_EVENT.TYPES.PILL_TOGGLE,
    collection: collectionInfo,
  });
};

const eventSorter = store => next => action => {
  switch (action.type) {
    case ACTIONS.SET_CURRENT_PRODUCT:
    case ACTIONS.SET_CURRENT_SIZE:
      viewProductDetails(store, next, action);
      break;
    case ACTIONS.SET_COLLECTION_PAGE_FILTER:
    case ACTIONS.CLEAR_COLLECTION_PAGE_FILTERS:
      productListFiltered(store, next, action);
      break;
    case ACTIONS.ADD_LINE_ITEMS_SUCCESS:
      addToCart(store, next, action);
      break;
    case ACTIONS.REMOVE_LINE_ITEM_SUCCESS:
      removeFromCart(store, next, action);
      break;
    case ACTIONS.UPDATE_LINE_ITEM_SUCCESS:
      updateLineItem(store, next, action);
      break;
    case ACTIONS.TOGGLE_CART:
      openCart(store, next, action);
      break;
    case ACTIONS.FETCH_CATALOG_FINISH:
      productListViewed(store, next, action);
      break;
    case ACTIONS.FETCH_CUSTOM_COLLECTION_SUCCESS:
      customCollectionProductListViewed(store, next, action);
      break;
    case ACTIONS.FETCH_PERSONALIZATIONS_SUCCESS:
      experimentsViewed(store, next, action);
      break;
    case ACTIONS.TRACK_BANNER_CLICK:
      globalBannerClicked(store, next, action);
      break;
    case ACTIONS.TRACK_COLLECTION_PILL_TOGGLE_CLICK:
      trackCollectionPillToggleClick(store, next, action);
      break;
    default:
      next(action);
  }
};

export default eventSorter;
